import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './SignUp.css';
import { registerUser } from '../apiService';

/**
 * Account creation.
 *
 * Collects only what the backend actually stores (firstName, lastName,
 * email, password). Company details and plan selection are intentionally
 * NOT here — there is no Organization/Subscription model yet, and standard
 * practice is to create the account first and collect that during
 * onboarding afterwards.
 */
function SignUp() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear a field's error as soon as the user starts fixing it.
    setErrors(prev => (prev[field] ? { ...prev, [field]: undefined } : prev));
    setServerError('');
  }

  function validate() {
    const next = {};
    if (!form.firstName.trim()) next.firstName = 'First name is required.';
    if (!form.lastName.trim()) next.lastName = 'Last name is required.';

    if (!form.email.trim()) {
      next.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      next.email = 'Enter a valid email address.';
    }

    if (!form.password) {
      next.password = 'Password is required.';
    } else if (form.password.length < 8) {
      next.password = 'Password must be at least 8 characters.';
    }

    if (!form.confirmPassword) {
      next.confirmPassword = 'Please confirm your password.';
    } else if (form.password !== form.confirmPassword) {
      next.confirmPassword = 'Passwords do not match.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    setSubmitting(true);
    const result = await registerUser({
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      email: form.email.trim(),
      password: form.password,
    });
    setSubmitting(false);

    if (result && result.success) {
      setSuccess(true);
    } else {
      const msg = (result && result.error) || '';
      if (/exist|already|duplicate/i.test(msg)) {
        setServerError('An account with that email already exists. Try signing in instead.');
      } else {
        setServerError(msg || 'Something went wrong creating your account. Please try again.');
      }
    }
  }

  if (success) {
    return (
      <div className="SignIn-page">
        <div className="SignIn-container">
          <h2 className="SignIn-title">Check your email</h2>
          <div className="section">
            <p style={{ lineHeight: 1.6 }}>
              We've sent a verification link to <strong>{form.email.trim()}</strong>.
              Open it to activate your account.
            </p>
            <p style={{ lineHeight: 1.6, color: '#666', fontSize: '14px' }}>
              Didn't get it? Check your spam folder, or contact support if it
              doesn't arrive shortly.
            </p>
          </div>
          <button
            type="button"
            className="SignIn-button"
            onClick={() => navigate('/login')}
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="SignIn-page">
      <div className="SignIn-container">
        <h2 className="SignIn-title">Create Your Account</h2>

        <form className="SignIn-form" onSubmit={handleSubmit} noValidate>
          <div className="section">
            <div className="form-group">
              <label htmlFor="firstName">First Name</label>
              <input
                id="firstName"
                type="text"
                autoComplete="given-name"
                value={form.firstName}
                onChange={e => update('firstName', e.target.value)}
              />
              {errors.firstName && <div className="field-error">{errors.firstName}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name</label>
              <input
                id="lastName"
                type="text"
                autoComplete="family-name"
                value={form.lastName}
                onChange={e => update('lastName', e.target.value)}
              />
              {errors.lastName && <div className="field-error">{errors.lastName}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={e => update('email', e.target.value)}
              />
              {errors.email && <div className="field-error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={e => update('password', e.target.value)}
              />
              {errors.password
                ? <div className="field-error">{errors.password}</div>
                : <div className="field-hint">At least 8 characters.</div>}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                value={form.confirmPassword}
                onChange={e => update('confirmPassword', e.target.value)}
              />
              {errors.confirmPassword && <div className="field-error">{errors.confirmPassword}</div>}
            </div>
          </div>

          {serverError && <div className="error-message">{serverError}</div>}

          <button type="submit" className="SignIn-button" disabled={submitting}>
            {submitting ? 'Creating account...' : 'Create Account'}
          </button>

          <p style={{ textAlign: 'center', marginTop: '14px', fontSize: '14px' }}>
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default SignUp;
